import { CommonDocumentEditorDetails } from '@primatoo-ms-organization';
import {
  ChapterPayload,
  DocumentPermission,
  EditorButtonConfiguration,
  EditorEditItem,
  enableEditorInternalStatusLog,
  PermissionDialog, PtEntity,
  StoreTemplateStatus,
  UiDocumentItem
} from '@pt/pt-models';
import { GetChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/GetChapterResponse';
import { DocResourceStatus } from 'it/primatoo/openapi/api/org/schemas/common/DocResourceStatus';
import { DocAccess } from 'it/primatoo/openapi/api/org/schemas/common/DocAccess';

export class EditorInternalStatus {
  /**
   * se valorizzato mostra i log solo dei widget con internalId inserito nella lista
   * @private
   */
  private readonly enabledLog = enableEditorInternalStatusLog;
  private _totalWidget: number;
  private _validWidgetId: number[];
  private _isResourceLockManagerEnabled!: boolean;
  private _actualEditorEditItem: EditorEditItem;
  private _isAutoSave: boolean;
  private readonly _entity!: PtEntity;
  private readonly treatmentEntity:PtEntity[] = [
    'treatments', // i trattamenti passano questo valore
    // 'treatmentsResponsible',
    // 'treatmentsSubResponsible',
    // 'treatmentsHolder',
    // 'projectCards'
  ];

  /**
   * isLoading è utilizzato dal editor dei documenti per notificare quando ha finito il caricamento interno
   * Ex. il caricamento della lista delle LegalEntity
   * @private
   */
    // private _isLoading: boolean;
  private _isDisabled: boolean;
  private _isInitializedTemplate: boolean;
  private _completeValue: number;
  private _isCompleted: boolean;
  private _aiAssessmentPermission: DocumentPermission;
  private _treatmentPermission: DocumentPermission;
  private _appAssessmentPermission: DocumentPermission;
  private _dpiaPermission: DocumentPermission;
  private _covidAssessmentPermission: DocumentPermission;
  private _globalMeasureChecksPermission: DocumentPermission;

  private _noChapterPermission: boolean;

  constructor(entity: PtEntity) {
    this._entity = entity;
    this._isAutoSave = false;
    this._isAutoSaveActive = false;
    this._isInitializedTemplate = false;
    this._lockAcquired = false;

    this._descriptionButton = new EditorButtonConfiguration();
    this._nameButton = new EditorButtonConfiguration();
    this._internalIdButton = new EditorButtonConfiguration();
    this._folderButton = new EditorButtonConfiguration();
    this._legalEntityButton = new EditorButtonConfiguration();

    this._noChapterPermission = false;
    this._treatmentCategoryButton = new EditorButtonConfiguration();
    this._departmentButton = new EditorButtonConfiguration();

    this._actualEditorEditItem = null;
    this._totalWidget = 0;
    this._validWidgetId = [];

    this._isResourceLockManagerEnabled = false;
  }

  private _editorReady = false;

  get editorReady(): boolean {
    return this._editorReady;
  }

  private _permissionDialog?: PermissionDialog;

  get permissionDialog(): PermissionDialog {
    return this._permissionDialog;
  }

  get noChapterPermission(): boolean {
    return this._noChapterPermission;
  }

  private _loadingPreview = false;
  private _loadingWord = false;
  private _internalIdEditable = true;

  get loadingPreview(): boolean {
    return this._loadingPreview;
  }

  get loadingWord(): boolean {
    return this._loadingWord;
  }

  private _details: CommonDocumentEditorDetails;

  get details(): CommonDocumentEditorDetails {
    return this._details;
  }

  get uiDocumentItem(): UiDocumentItem {
    const response: UiDocumentItem = {
      docId: this._details.docId,
      name: this._details.name,
      status: this._details.status,
      creatorUserId: this._details.creatorUserId,
      docAccess: DocAccess.FULL,
      treatmentType: this._details.treatmentType,
      parentId: this._details.parentId,
      description: this._details.description
    }
    return response;
  }

  private _descriptionButton: EditorButtonConfiguration;

  get descriptionButton(): EditorButtonConfiguration {
    return this._descriptionButton;
  }

  private _nameButton: EditorButtonConfiguration;

  get nameButton(): EditorButtonConfiguration {
    return this._nameButton;
  }

  private _internalIdButton: EditorButtonConfiguration;

  get internalIdButton(): EditorButtonConfiguration {
    return this._internalIdButton;
  }

  private _folderButton: EditorButtonConfiguration;

  get folderButton(): EditorButtonConfiguration {
    return this._folderButton;
  }

  private _treatmentCategoryButton: EditorButtonConfiguration;

  private _departmentButton: EditorButtonConfiguration;
  private _processOwnerButton: EditorButtonConfiguration;


  get treatmentCategoryButton(): EditorButtonConfiguration {
    return this._treatmentCategoryButton;
  }

  get departmentButton(): EditorButtonConfiguration {
    return this._departmentButton;
  }

  get processOwnerButton(): EditorButtonConfiguration {
    return this._processOwnerButton;
  }

  private _legalEntityButton: EditorButtonConfiguration;


  private _actualEditModeWidgetId: number;

  get actualEditModeWidgetId(): number {
    return this._actualEditModeWidgetId;
  }

  private _showNotification = true;

  get showNotification(): boolean {
    return this._showNotification;
  }

  private _showSidebar = true;
  private _showSummary = false;

  get showSummary(): boolean {
    return this._showSummary;
  }
  get showSidebar(): boolean {
    return this._showSidebar;
  }

  private _lockAcquired: boolean;

  get lockAcquired(): boolean {
    return this._lockAcquired;
  }

  private _isAutoSaveActive: boolean;

  get isAutoSaveActive(): boolean {
    return this._isAutoSaveActive;
  }

  private _i18nErrorMessage: string;

  get i18nErrorMessage(): string {
    return this._i18nErrorMessage;
  }

  get userCheck(): boolean {
    return this._details?.userCheck || false;
  }

  get isDraft(): boolean {
    return this._details?.status === DocResourceStatus.DRAFT;
  }


  get isPublishedDocument(): boolean {
    return this._details?.status === DocResourceStatus.PUBLISHED;
  }

  get documentPermission(): DocumentPermission | null {
    switch (true) {
      case !!this._aiAssessmentPermission:
        return this._aiAssessmentPermission;
      case !!this._appAssessmentPermission:
        return this._appAssessmentPermission;
      case !!this._covidAssessmentPermission:
        return this._covidAssessmentPermission;
      case !!this._globalMeasureChecksPermission:
        return this._globalMeasureChecksPermission;
      case !!this._treatmentPermission:
        return this._treatmentPermission;
      case !!this._dpiaPermission:
        return this._dpiaPermission;
    }
    return null;
  }

  get info(): EditorInternalStatusInfo {
    return {
      isPublishedDocument: this.isPublishedDocument,
      totalWidget: this._totalWidget,
      validWidgetId: this._validWidgetId,
      isResourceLockManagerEnabled: this._isResourceLockManagerEnabled,
      userCheck: this.userCheck,
      actualEditModeWidgetId: this.actualEditModeWidgetId,
      isAutoSave: this._isAutoSaveActive,
      isDraft: this.isDraft,
      showNotification: this.showNotification,
      showSidebar: this.showSidebar,
      isDisabled: this._isDisabled,
      lockAcquired: this._lockAcquired,
      isAutoSaveActive: this._isAutoSaveActive,
      isInitializedTemplate: this._isInitializedTemplate,
      completeValue: this._completeValue,
      isCompleted: this._isCompleted,
      i18nErrorMessage: this._i18nErrorMessage
    }
  }

  static isChangeCompletedStatus(totalWidget: number, validWidget: number, prevCompletedStatus: number): boolean {
    const actualValue = EditorInternalStatus.calculatePercentageOfCompletion(totalWidget, validWidget);
    if (prevCompletedStatus !== null && prevCompletedStatus !== undefined) {
      return prevCompletedStatus !== actualValue;
    } else {
      return actualValue !== 0;
    }
  }

  static calculatePercentageOfCompletion(totalWidget: number, validWidget: number): number {
    if (totalWidget !== null && totalWidget !== undefined && totalWidget > 0 && validWidget !== null && validWidget !== undefined) {
      return Math.round(validWidget / totalWidget * 100);
    }
    return 0;
  }

  setLockAcquired(lockAcquired: boolean) {
    if (lockAcquired !== this._lockAcquired) {
      this._lockAcquired = lockAcquired ? lockAcquired : false;
      this.checkButtonPermission();
    }
  }

  setLoadingPreview(value: boolean) {
    this._loadingPreview = value;
  }

  setLoadingWord(value: boolean) {
    this._loadingWord = value;
  }

  setInternalIdEditable(internalIdEditable: boolean) {
    if (this._internalIdEditable !== internalIdEditable) {
      this._internalIdEditable = internalIdEditable;
      this.checkButtonPermission()
    }
  }

  public checkButtonPermission() {
    this._isDisabled = this.checkIsDisabled();
    this._descriptionButton = this.checkDescriptionButton();
    this._nameButton = this.checkButton(EditorEditItem.NAME);
    this._internalIdButton = this.checkButton(EditorEditItem.INTERNAL_ID);
    this._treatmentCategoryButton = this.checkButton(EditorEditItem.TREATMENT_CATEGORY);
    this._departmentButton = this.checkButton(EditorEditItem.DEPARTMENT);
    this._processOwnerButton = this.checkButton(EditorEditItem.PROCESS_OWNER);

    this._folderButton = this.checkFolderButton();
    this._i18nErrorMessage = this.geti18nErrorIfExist();
    this._completeValue = this.checkCompleteValue();
    this._isCompleted = this._completeValue == 100;
    this._isAutoSaveActive = this.isAutoSaveActiveController();
    this._isResourceLockManagerEnabled = this.documentPermission?.writeAccess;
    this._legalEntityButton = this.checkLegalEntityButton();
  }

  private checkLegalEntityButton(): EditorButtonConfiguration {
    // DEFAULT
    let disabledCancel = true;
    let disabledSave = true;
    let disabledEdit = true;
    let showEdit = false;
    let showSave = false;
    let showCancel = false;

    if (this.documentPermission?.writeAccess && this.isDraft) {
      if (this._isAutoSave) {
        /**
         * Nella modalità AUTOSAVE posso modificare se ho il lock, se è draft e se è inizializzato
         */

        if (this._lockAcquired && this._isInitializedTemplate) {
          disabledSave = false;
          showSave = true;
        } else {
          // Mantengo il DEFAULT
        }

      } else {
        /**
         * Nella modalità NO AUTOSAVE posso modificare se ho il lock, se è draft e se è inizializzato E se NON CI SONO WIDGET IN MODIFICA
         */
        if (this._lockAcquired && this._isInitializedTemplate && !Number.isSafeInteger(this._actualEditModeWidgetId)) {
          switch (true) {
            case this._actualEditorEditItem === null:
              showEdit = true;
              disabledEdit = false;
              break;
            // sono i edit della descrizione
            case this._actualEditorEditItem === EditorEditItem.LEGAL_ENTITY:
              showEdit = false;
              disabledEdit = true;
              showCancel = true;
              showSave = true;
              disabledCancel = false;
              disabledSave = false;
              break;
            default:
              // Sto modificando un'altra cosa
              showEdit = true;
              disabledEdit = true;
              showCancel = false;
              showSave = false;
              disabledCancel = true;
              disabledSave = true;
          }
        } else {
          // DEFAULT
        }
      }
    }
    return EditorButtonConfiguration.create(showEdit, showCancel, showSave, disabledEdit, disabledCancel, disabledSave, this._isAutoSave);
  }


  toggleShowSidebar() {
    this._showSidebar = !this._showSidebar;
    if (this._showSidebar) {
      this._showSummary = false;
    }
  }

  toggleShowSummary() {
    this._showSummary = !this._showSummary;
    if (this._showSummary) {
      this._showSidebar = false;
    }
  }

  /**
   * @param status
   */
  changeStoreTemplateStatus(status: StoreTemplateStatus) {
    if (status) {
      this._editorReady = status.editorReady;

      let changeValue = false;
      switch (true) {
        case JSON.stringify(status.validWidgetId) !== JSON.stringify(this._validWidgetId):
        case status.totalWidget !== this._totalWidget:
        case status.editorReady !== this._isInitializedTemplate:
        case status.activedEditWidgetId !== this._actualEditModeWidgetId:
        case status.activedEditTreatment !== this._actualEditorEditItem:
          changeValue = true;
          break;
      }
      if (changeValue) {
        this._validWidgetId = status.validWidgetId;
        this._totalWidget = status.totalWidget;
        this._isInitializedTemplate = status.editorReady;
        this._actualEditModeWidgetId = status.activedEditWidgetId;
        this._actualEditorEditItem = status.activedEditTreatment;
        this.checkButtonPermission();
      }
    }
  }

  changeDetails(details: CommonDocumentEditorDetails) {
   // console.log('Change details ', details);
    if (details?.status) {
      this._details = JSON.parse(JSON.stringify(details));
      this.checkButtonPermission();
    }
  }

  changeChapter(chapter: GetChapterResponse) {
    if (chapter) {
      const payload: ChapterPayload = chapter.payload;
      const currentIsAutoSave: boolean = payload.autoSave ? payload.autoSave : false;
      if (this._isAutoSave !== currentIsAutoSave) {
        this._isAutoSave = currentIsAutoSave;
        this.checkButtonPermission();
      }
    }
  }

  setAiAssessmentPermission(permission: DocumentPermission) {
    this.resetPermission();
    this._aiAssessmentPermission = permission;
    this.checkButtonPermission();
    // this.logPermission();
  }

  setAppAssessmentPermission(permission: DocumentPermission) {
    this.resetPermission();
    this._appAssessmentPermission = permission;
    this.checkButtonPermission();
    // this.logPermission();
  }

  setCovidAssessmentPermission(permission: DocumentPermission) {
    this.resetPermission();
    this._covidAssessmentPermission = permission;
    this.checkButtonPermission();
    // this.logPermission();
  }

  setTreatmentPermission(permission: DocumentPermission) {
    this.resetPermission();
    this._treatmentPermission = permission;
    this.checkButtonPermission();
    // this.logPermission();
  }

  private geti18nErrorIfExist(): string | null {

    switch (!!this.documentPermission) {
      case !this.documentPermission?.writeAccess:
        return 'MESSAGE.MISSING_WRITE_PERMISSION';

      case !this._lockAcquired && this._details && this._details.status === DocResourceStatus.DRAFT:
        return 'MESSAGE.RESOURCE_LOCKED';
    }
    return null;
  }

  private showLog(): boolean {
    return this.enabledLog;
  }

  private checkIsDisabled() {
    switch (true) {
      case !this.documentPermission?.writeAccess:
      case !this.isDraft:
        return true;

      case !this._lockAcquired:
        return true;
    }
    return false;
  }

  private checkDescriptionButton(): EditorButtonConfiguration {
    // DEFAULT
    let disabledCancel = true;
    let disabledSave = true;
    let disabledEdit = true;
    let showEdit = false;
    let showSave = false;
    let showCancel = false;

    if (this.documentPermission?.writeAccess && this.isDraft) {
      if (this._isAutoSave) {
        /**
         * Nella modalità AUTOSAVE posso modificare se ho il lock, se è draft e se è inizializzato
         */

        if (this._lockAcquired && this._isInitializedTemplate) {
          disabledSave = false;
          showSave = true;
        } else {
          // Mantengo il DEFAULT
        }

      } else {
        /**
         * Nella modalità NO AUTOSAVE posso modificare se ho il lock, se è draft e se è inizializzato E se NON CI SONO WIDGET IN MODIFICA
         */
        if (this._lockAcquired && this._isInitializedTemplate && !Number.isSafeInteger(this._actualEditModeWidgetId)) {
          switch (true) {
            case this._actualEditorEditItem === null:
              showEdit = true;
              disabledEdit = false;
              break;
            // sono i edit della descrizione
            case this._actualEditorEditItem === EditorEditItem.DESCRIPTION:
              showEdit = false;
              disabledEdit = true;
              showCancel = true;
              showSave = true;
              disabledCancel = false;
              disabledSave = false;
              break;
            default:
              // Sto modificando un'altra cosa
              showEdit = true;
              disabledEdit = true;
              showCancel = false;
              showSave = false;
              disabledCancel = true;
              disabledSave = true;
          }
        } else {
          // DEFAULT
        }
      }
    }
    return EditorButtonConfiguration.create(showEdit, showCancel, showSave, disabledEdit, disabledCancel, disabledSave, this._isAutoSave);
  }

  private checkButton(item: EditorEditItem): EditorButtonConfiguration {
    let disabledCancel = true;
    let disabledSave = true;
    let disabledEdit = true;
    let showEdit = false;
    let showSave = false;
    let showCancel = false;

    if (this.treatmentEntity.includes(this._entity) && item === EditorEditItem.INTERNAL_ID && !this._internalIdEditable) {
      return {
        autosave: false,
        disabledCancel: true,
        disabledSave: true,
        disabledEdit: true,
        showEdit: false,
        showSave: false,
        showCancel: false,
      } as EditorButtonConfiguration;
    }

    if (this.documentPermission?.writeAccess) {
      switch (true) {
        // queste due condizioni sono richieste per abilitare un qualunque pulsante
        case this._lockAcquired && this.isDraft && this._isInitializedTemplate && !Number.isSafeInteger(this._actualEditModeWidgetId):
          switch (true) {
            case this._actualEditorEditItem === null:
              showEdit = true;
              disabledEdit = false;
              showCancel = false;
              showSave = false;
              disabledCancel = true;
              disabledSave = true;
              break;
            // Sto editando me stesso
            case this._actualEditorEditItem === item:
              showEdit = false;
              disabledEdit = true;
              showCancel = true;
              showSave = true;
              disabledCancel = false;
              disabledSave = false;
              break;
            default:
              showEdit = true;
              disabledEdit = true;
              showCancel = false;
              showSave = false;
              disabledCancel = true;
              disabledSave = true;
          }
          break;

        default:
          showEdit = true;
          showSave = false;
          showCancel = false;
          disabledCancel = true;
          disabledEdit = true;
          disabledSave = true;
      }
    }
    return {
      autosave: false,
      disabledCancel,
      disabledSave,
      disabledEdit,
      showEdit,
      showSave,
      showCancel
    } as EditorButtonConfiguration;


  }

  private checkFolderButton(): EditorButtonConfiguration {
    let disabledCancel = true;
    let disabledSave = true;
    let disabledEdit = true;
    let showEdit = false;
    let showSave = false;
    let showCancel = false;

    if (this.documentPermission?.manageWorkspaceAccess) {
      switch (true) {
        // queste due condizioni sono richieste per abilitare un qualunque pulsante
        case this._lockAcquired && this._isInitializedTemplate && !Number.isSafeInteger(this._actualEditModeWidgetId):
          // ho tolto  && this._isDraft perchè le folder sono modificabili anche se l'entità è stata pubblicata
          switch (true) {
            case this._actualEditorEditItem === null:
              showEdit = true;
              disabledEdit = !this.documentPermission?.writeAccess;
              showCancel = false;
              showSave = false;
              disabledCancel = true;
              disabledSave = true;
              break;
            // Sto editando me stesso
            case this._actualEditorEditItem === EditorEditItem.FOLDER:
              showEdit = false;
              disabledEdit = true;
              showCancel = true;
              showSave = true;
              disabledCancel = false;
              disabledSave = false;
              break;
            default:
              showEdit = true;
              disabledEdit = true;
              showCancel = false;
              showSave = false;
              disabledCancel = true;
              disabledSave = true;
          }
          break;

        default:
          showEdit = true;
          showSave = false;
          showCancel = false;
          disabledCancel = true;
          disabledEdit = true;
          disabledSave = true;
      }
    }

    return {
      autosave: false,
      disabledCancel,
      disabledSave,
      disabledEdit,
      showEdit,
      showSave,
      showCancel
    } as EditorButtonConfiguration;
  }

  //
  setGlobalMeasureChecksPermission(permission: DocumentPermission) {
    this.resetPermission();
    this._globalMeasureChecksPermission = permission;
    this.checkButtonPermission();
    // this.logPermission();
  }

  //
  // setDpiaAssessmentPermission(permission: DpiaPermission) {
  //   this.resetPermission();
  //   this._dpiaPermission = permission;
  //   this.checkButtonPermission();
  //   // this.logPermission();
  // }

  private checkCompleteValue() {
    const value = EditorInternalStatus.calculatePercentageOfCompletion(
      this._totalWidget,
      this._validWidgetId?.length
    );
    // console.log('Check Complete value ', value);
    return value;
  }

  private isAutoSaveActiveController(): boolean {
    return this._isInitializedTemplate &&
      this._lockAcquired &&
      this._isAutoSave &&
      this.documentPermission?.writeAccess;
  }

  private resetPermission() {
    this._aiAssessmentPermission = undefined;
    this._treatmentPermission = undefined;
    this._appAssessmentPermission = undefined;
    this._dpiaPermission = undefined;
    this._covidAssessmentPermission = undefined;
    this._globalMeasureChecksPermission = undefined;
  }

  setNoChapterPermission($event: boolean) {
    this._noChapterPermission = $event;
  }
}

export interface EditorInternalStatusInfo {
  isPublishedDocument: boolean;
  totalWidget: number;
  validWidgetId: number[];
  isResourceLockManagerEnabled: boolean;
  userCheck: boolean;
  actualEditModeWidgetId: number;
  isAutoSave: boolean;
  isDraft: boolean;
  showNotification: boolean;
  showSidebar: boolean;
  isDisabled: boolean;
  lockAcquired: boolean;
  isAutoSaveActive: boolean;
  isInitializedTemplate: boolean;
  completeValue: number;
  isCompleted: boolean;
  i18nErrorMessage: string;
}
